// Making and withdrawing links that work without a login.
//
// The access code appears exactly once, at creation, because only its hash is kept. Everything else on
// this panel is editable after the fact by turning the link off and making another — deliberately, so
// that "who can still open this" is answered by a short list of live links rather than by archaeology.

import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { formatDate } from '/imports/utils';
import { css } from '/imports/ui/css';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { Input } from '/imports/ui/Input';
import { Modal } from '/imports/ui/Modal';
import { Select } from '/imports/ui/Select';
import { toastOnError } from '/imports/ui/utils';
import { useMethod } from '/imports/hooks';
import {
  DriveShareRow, driveCreateShare, driveListShares, driveRevokeShare, driveShareVisits,
} from '/imports/api/drive';

const DriveShareModalStyled = styled.div`
  .make { display: grid; gap: 0.6rem; margin-bottom: 1rem; }
  .make .row { display: flex; align-items: center; gap: 0.5rem; }
  .make .row > label { flex: 1; font-size: 0.85rem; display: flex; align-items: center; gap: 0.4rem; }
  .make select { width: auto; }
  .hint { font-size: 0.76rem; color: ${css.grey4}; margin-top: -0.3rem; }
  .code {
    background: ${css.grey9}; border: 1px solid ${css.grey7}; border-radius: ${css.inputBorderRadius};
    padding: 0.5rem 0.7rem; font-size: 0.85rem; margin-bottom: 0.75rem;
  }
  .code b { font-family: monospace; font-size: 1rem; letter-spacing: 0.08em; }
  h3 {
    font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: ${css.grey4};
    margin: 1rem 0 0.4rem; padding-bottom: 0.2rem; border-bottom: 1px solid ${css.grey7};
  }
  .share { display: flex; align-items: baseline; gap: 0.5rem; padding: 0.45rem 0; border-bottom: 1px solid ${css.grey8}; font-size: 0.85rem; }
  .share .body { flex: 1; min-width: 0; }
  .share .what { display: flex; align-items: center; gap: 0.4rem; }
  .share .meta { color: ${css.grey4}; font-size: 0.76rem; }
  .share .tools { display: flex; gap: 0.25rem; align-items: center; }
  .visits { font-size: 0.78rem; color: ${css.grey3}; padding: 0.2rem 0 0.5rem 0.4rem; }
  .visits div { padding: 0.1rem 0; }
`;

const stateTone: { [state: string]: string } = { live: 'green', revoked: 'grey', expired: 'grey', locked: 'amber' };
const stateLabel = (state: DriveShareRow['state']) => {
  switch (state) {
    case 'revoked': return strings.ShareStateRevoked();
    case 'expired': return strings.ShareStateExpired();
    case 'locked': return strings.ShareStateLocked();
    default: return strings.ShareStateLive();
  }
};

export interface DriveShareModalProps {
  target: { kind: 'folder' | 'file', id: string };
  name: string;
  onClose(): void;
}

export const DriveShareModal = (props: DriveShareModalProps) => {
  const [reload, setReload] = useState(0);
  const [shares, loading] = useMethod(driveListShares, { target: props.target }, [props.target.id, reload]);
  const [label, setLabel] = useState<string>();
  const [days, setDays] = useState('30');
  const [withCode, setWithCode] = useState(true);
  const [requireEmail, setRequireEmail] = useState(false);
  const [code, setCode] = useState<string>();
  const [openVisits, setOpenVisits] = useState<string>();
  const [visits] = useMethod(driveShareVisits, { _id: openVisits || '' }, [openVisits], () => !!openVisits);

  const copy = toastOnError(async (url: string) => {
    await navigator.clipboard.writeText(url);
    toast(strings.LinkCopied());
  });

  const create = toastOnError(async () => {
    const made = await driveCreateShare.call({
      target: props.target,
      label: label?.trim() || undefined,
      expiresInDays: Number(days) || undefined,
      withCode,
      requireEmail,
    });
    setCode(made.code);
    setLabel(undefined);
    setReload(reload + 1);
    await copy(made.url);
  });

  const revoke = toastOnError(async (share: DriveShareRow) => {
    await driveRevokeShare.call({ _id: share._id });
    setReload(reload + 1);
  });

  return (
    <Modal title={`${strings.ShareLink()} — ${props.name}`} onClose={props.onClose}>
      <DriveShareModalStyled>
        <div className="make">
          <Input placeholder={strings.ShareLabel()} value={label || ''} onChange={(value) => setLabel(value)} />
          <div className="row">
            <label htmlFor="share-expiry">{strings.ShareExpiry()}</label>
            <Select
              id="share-expiry"
              value={days}
              options={[
                { value: '1', label: '1 day' },
                { value: '7', label: '7 days' },
                { value: '30', label: '30 days' },
                { value: '90', label: '90 days' },
                { value: '0', label: strings.NeverExpires() },
              ]}
              onChange={(value) => setDays(value)}
            />
          </div>
          <div className="row">
            <label htmlFor="share-code">
              <input id="share-code" type="checkbox" checked={withCode} onChange={(e) => setWithCode(e.target.checked)} />
              {strings.ShareWithCode()}
            </label>
          </div>
          <div className="hint">{strings.ShareWithCodeHint()}</div>
          <div className="row">
            <label htmlFor="share-email">
              <input id="share-email" type="checkbox" checked={requireEmail} onChange={(e) => setRequireEmail(e.target.checked)} />
              {strings.ShareRequireEmail()}
            </label>
          </div>
          <div className="hint">{strings.ShareRequireEmailHint()}</div>
          <div><Button onClick={() => create()}>{strings.CreateShareLink()}</Button></div>
        </div>

        {code && (
          <div className="code">{strings.ShareAccessCode()}: <b>{code}</b> — {strings.ShareCodeOnce()}</div>
        )}

        <h3>{strings.ShareLinks()}</h3>
        {loading && !shares && <div className="muted">{strings.Loading()}</div>}
        {shares?.map((share) => (
          <React.Fragment key={share._id}>
            <div className="share">
              <span className="body">
                <span className="what">
                  <span className={`chip ${stateTone[share.state]}`}>{stateLabel(share.state)}</span>
                  {share.label || share.targetName}
                </span>
                <span className="meta">
                  {share.expiresAt ? formatDate(share.expiresAt) : strings.NeverExpires()}
                  {share.hasCode ? ` · ${strings.ShareAccessCode()}` : ''}
                  {` · ${strings.Visits()}: ${share.visitCount}`}
                  {share.createdByName ? ` · ${share.createdByName}` : ''}
                </span>
              </span>
              <span className="tools">
                <button type="button" className="icon-btn" aria-label={strings.CopyLink()} onClick={() => copy(share.url)}>
                  <Icon icon="link2" size={16} fill={css.grey3} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={strings.Visits()}
                  onClick={() => setOpenVisits(openVisits === share._id ? undefined : share._id)}
                >
                  <Icon icon="visibility" size={16} fill={css.grey3} />
                </button>
                {!share.revokedAt && (
                  <button type="button" className="icon-btn" aria-label={strings.Revoke()} onClick={() => revoke(share)}>
                    <Icon icon="cancel" size={16} fill={css.red4} />
                  </button>
                )}
              </span>
            </div>
            {openVisits === share._id && (
              <div className="visits">
                {visits?.map((visit) => (
                  <div key={visit._id}>
                    {formatDate(visit.at)}
                    {visit.email ? ` · ${visit.email}` : ''}
                    {visit.ipAddr ? ` · ${visit.ipAddr}` : ''}
                    {visit.fileName ? ` · ${visit.fileName}` : ''}
                  </div>
                ))}
                {!visits?.length && <div className="muted">{strings.Visits()}: 0</div>}
              </div>
            )}
          </React.Fragment>
        ))}
        {shares && !shares.length && <div className="muted">{strings.NoShareLinks()}</div>}
      </DriveShareModalStyled>
    </Modal>
  );
};
